import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MarketingNav, MarketingFooter } from "@/components/marketing/MarketingChrome";
import { Button } from "@/components/ui/button";
import { PAGE_BANNERS } from "@/lib/assets";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  HelpCircle, Users, Building2, GraduationCap, LineChart, Sparkles, ArrowRight,
} from "lucide-react";

const CATEGORIES = [
  {
    id: "general",
    label: "General",
    icon: Sparkles,
    blurb: "Why GPA+ exists and what makes it different.",
    faqs: [
      {
        q: "Why was GPA+ created?",
        a: "GPA+ was born from a simple, uncomfortable truth we kept hearing from two sides at once. Our students were graduating with solid transcripts yet struggling to articulate what they could actually do — and employers told us, repeatedly, that a GPA on a résumé told them almost nothing about whether a candidate could communicate, solve problems, collaborate, or adapt on day one. Hiring managers described sorting through stacks of qualified-on-paper applicants while still failing to find the right talent, because the single number they were handed measured recall of content, not readiness for work. We built GPA+ to close that gap: to translate the durable, transferable skills a student genuinely builds across their coursework into evidence an employer can trust, and to give students language and proof for strengths they never knew how to show.",
      },
      {
        q: "What makes GPA+ different?",
        a: "Most 'skills' tools are either self-reported quizzes or opaque black boxes. GPA+ is neither. First, it is grounded in the actual learning outcomes of real coursework — skills are weight-mapped from course SLOs, rolled up into three capacity domains, and expressed as a Transferable Skills Benchmark (TSB) on a transparent 1.0–4.0 scale (also shown as 0–100). Second, it is career-aware: the same underlying skills are re-weighted against the demands of 18 different career fields, so a student can instantly see how their profile aligns with accounting versus nursing versus IT. Third, employer verification is a separate, clearly-labeled, reversible signal — never silently blended into a student's score, so trust is preserved on both sides. Finally, an AI Skills Coach turns the data into a plan: concrete, personalized next steps rather than a static report.",
      },
      {
        q: "A GPA is good — so why isn't it enough?",
        a: "A GPA is a meaningful signal of academic discipline and content mastery, and we are not replacing it. But it compresses years of growth into one number that answers only one question: 'Did this student master the material in these courses?' It cannot tell an employer whether that student can write clearly under deadline, lead a team through disagreement, break down an ambiguous problem, or keep learning when the job changes. Those durable skills — the ones that transfer across roles, industries, and an entire career — are exactly what predict long-term success, and they are invisible on a transcript. GPA+ makes them visible, measurable, and actionable. Think of it this way: GPA tells you the grade; GPA+ tells you what the learner can do with it.",
      },
      {
        q: "What is the Transferable Skills Benchmark (TSB)?",
        a: "The TSB is GPA+'s headline metric: a single, research-backed benchmark that summarizes a learner's proficiency across 10 durable skills organized into 3 domains. It's scored on a 1.0–4.0 scale mapped to four bands — Emerging, Developing, Proficient, and Advanced — and also expressed as a 0–100 figure for quick reading. Crucially, the TSB is dynamic: because different careers demand different skill mixes, the benchmark recalculates when you choose a target career field, showing alignment, strengths, and gaps for that specific path.",
      },
      {
        q: "Who is behind GPA+?",
        a: "GPA+ is a Lone Star College initiative, envisioned by Dr. Lee Ann Nutt, President of Lone Star College–Tomball. It draws on nationally-recognized durable-skills research (including O*NET and America Succeeds' Durable Skills framework) and is designed to align with the workforce-readiness movement championed by organizations like UpSkill America — an employer-led effort to make skills, not just credentials, the currency of opportunity.",
      },
      {
        q: "Is this using real student data?",
        a: "No. This is a working prototype built for leadership review. It runs on carefully-constructed mock personas and curated, research-derived career profiles — there is no real student PII. The architecture, however, is built to integrate with live systems (D2L/Brightspace) and institutional SSO when it moves beyond the prototype stage.",
      },
    ],
  },
  {
    id: "students",
    label: "For Students",
    icon: Users,
    blurb: "Understand, grow, and prove your skills.",
    faqs: [
      {
        q: "What do I actually get as a student?",
        a: "You get a clear picture of your durable skills and a plan to grow them. Your dashboard shows your Transferable Skills Benchmark across all 10 skills and 3 domains, highlights your strongest areas, and flags the skills worth developing next. You also get a personalized AI Skills Coach, a Learning Academy with targeted growth resources, and — when you opt in — a way to share verified skills with employers.",
      },
      {
        q: "How does the 'what-if' career explorer work?",
        a: "Your underlying skill scores stay exactly the same, but different careers value different skills. The what-if explorer lets you pick a target career field and instantly see how your same profile aligns with that path — your overall benchmark, strengths, and gaps all recalculate against that career's real skill demands. It's a pressure-free way to explore majors and careers before you commit, and to understand precisely which skills to build for the path you want.",
      },
      {
        q: "How are my skill scores calculated?",
        a: "Scores are derived from the learning outcomes of your coursework, not from a personality quiz. Course-level outcomes are weight-mapped to the 10 skills, aggregated up into the 3 domains, and rolled into your TSB. Each skill lands in one of four bands — Emerging, Developing, Proficient, or Advanced — so you can see not just a number but what that number means and what 'the next level' looks like.",
      },
      {
        q: "What is the AI Skills Coach and how can it help me?",
        a: "The AI Skills Coach is a context-aware assistant that can see your skill profile (with your permission) and gives you specific, personalized guidance — which skills to prioritize for your target career, what a realistic growth plan looks like, how to talk about your strengths in interviews, and how coursework connects to real jobs. It turns a static score into an ongoing conversation and a concrete action plan.",
      },
      {
        q: "Will employers see my scores automatically?",
        a: "No. Nothing is shared without your explicit opt-in. GPA+ is FERPA-aligned and built on role-based access controls. You decide whether to make a profile visible to employers, and any employer verification is a separate, clearly-labeled signal that you can see and that is fully reversible.",
      },
      {
        q: "How do I raise my benchmark?",
        a: "Because scores are tied to demonstrated learning, you grow your benchmark by building real evidence: targeted courses, projects, internships, and Learning Academy modules that strengthen specific skills. Your Coach and dashboard point you to the highest-leverage moves for your chosen career, so your effort goes where it counts most.",
      },
    ],
  },
  {
    id: "employers",
    label: "For Employers",
    icon: Building2,
    blurb: "Find and verify the right talent.",
    faqs: [
      {
        q: "How does GPA+ help me find the right talent?",
        a: "Employers consistently tell us the problem isn't a shortage of applicants — it's a shortage of signal. A GPA doesn't tell you whether a candidate can communicate, collaborate, or solve problems in your environment. GPA+ gives you a skills-first view: opt-in candidate profiles benchmarked across 10 durable skills, aligned to the specific skill demands of your roles. Instead of guessing from a transcript, you can screen for the readiness that actually predicts on-the-job success.",
      },
      {
        q: "What does 'employer-verified' mean, and why is it separate?",
        a: "When you work with an intern or student, you can submit a structured 360 review of the skills you observed. That verification is stored as its own clearly-labeled, individually-weighted signal — it is never silently folded into the student's academic score. This keeps the system honest for everyone: students trust that their benchmark reflects their learning, and employers trust that a 'verified' badge means a real observation from a real workplace. Verification is also traceable and reversible, so the record stays accurate over time.",
      },
      {
        q: "How is this aligned with skills-first hiring?",
        a: "GPA+ is built for the skills-first movement that organizations like UpSkill America champion — the shift from screening on credentials alone to hiring on demonstrated capabilities. Our framework is grounded in durable-skills research (O*NET, America Succeeds), so the skills you see map to language and competencies your talent teams already use, making it easier to widen your pipeline and evaluate candidates fairly.",
      },
      {
        q: "Can I define the skills that matter most for my roles?",
        a: "Yes — that's the point of career-field weighting. Every role emphasizes a different mix of the 10 skills. GPA+ lets you view candidate profiles through the lens of a specific field's skill demands, so alignment, strengths, and gaps are calculated against what your role actually needs, not a generic average.",
      },
      {
        q: "How do I know the data is credible?",
        a: "The scoring engine is transparent, not a black box. Skills trace back to real course learning outcomes, benchmarks use a published 1.0–4.0 scale with defined proficiency bands, and employer verification is a distinct signal you can audit. Nothing about a student is shared without their opt-in consent, keeping the platform trustworthy for candidates and defensible for your organization.",
      },
    ],
  },
  {
    id: "educators",
    label: "For Educators",
    icon: GraduationCap,
    blurb: "See gaps, advise with evidence.",
    faqs: [
      {
        q: "How does GPA+ support advising and teaching?",
        a: "Advisors and faculty get an evidence-based view of each student's durable skills — not just their grades. You can see where a student is strong, where the gaps are, and how their profile aligns with their intended career, which turns advising from a generic conversation into a targeted, data-informed plan. It helps you spot who needs which intervention, and when.",
      },
      {
        q: "How do course outcomes connect to the skills?",
        a: "GPA+ weight-maps course student learning outcomes (SLOs) to the 10 transferable skills. That means the skills students build in your classroom are made visible and cumulative — a student's benchmark reflects the durable capabilities their program actually develops, and you can see how individual courses contribute to the bigger picture of readiness.",
      },
      {
        q: "Does this replace grades or academic judgment?",
        a: "Not at all. GPA+ complements grades. A grade certifies mastery of course content; the TSB surfaces the transferable skills that content-mastery also builds but that a grade can't show. Together they give a fuller, fairer portrait of a learner. Your academic judgment remains central — GPA+ simply gives you better instrumentation.",
      },
      {
        q: "Can I use this to design targeted interventions?",
        a: "Yes. Because skills are broken into 10 named competencies across 3 domains and four proficiency bands, you can identify exactly which skill a student needs to move — say, from Developing to Proficient — and point them to specific Learning Academy modules, projects, or coursework. Advising becomes proactive rather than reactive.",
      },
      {
        q: "How does this fit the broader workforce-readiness movement?",
        a: "GPA+ reflects the same philosophy driving national efforts like UpSkill America and the durable-skills research from America Succeeds: education is most powerful when it explicitly develops and documents the skills that transfer to work and life. GPA+ gives educators a way to participate in that movement with their own students, using their own curriculum.",
      },
    ],
  },
  {
    id: "administrators",
    label: "For Administrators",
    icon: LineChart,
    blurb: "Program insight and equity analytics.",
    faqs: [
      {
        q: "What can administrators see and do?",
        a: "Administrators get cohort-level insight: distributions of skills across programs, comparisons between programs, and equity-gap analysis to ensure every student population is being served well. It's designed to answer institutional questions — where are our students strong, where are the systemic gaps, and how do outcomes compare across programs — with data rather than anecdote.",
      },
      {
        q: "How does GPA+ support institutional goals and reporting?",
        a: "By making durable skills measurable at scale, GPA+ lets leadership connect curriculum to workforce readiness in a way a GPA distribution never could. It supports strategic-plan goals around employability and equity, and is architected to enable institutional reporting and exports as it matures beyond the prototype.",
      },
      {
        q: "How are privacy and access controlled?",
        a: "GPA+ is FERPA-aligned and built on role-based access control (RBAC). Students, advisors, administrators, and employers each see only what their role permits, student data is shared with employers only on opt-in, and every verification signal is traceable and reversible. Governance and trust are designed in, not bolted on.",
      },
      {
        q: "Can this integrate with our existing systems?",
        a: "The prototype runs on mock data, but the architecture is built for integration — designed to connect with D2L/Brightspace for learning outcomes and with institutional SSO for authentication. The goal is for GPA+ to sit on top of the systems you already run, not to become one more silo.",
      },
      {
        q: "How does this advance equity?",
        a: "Skills-first measurement can widen opportunity: it surfaces capabilities that traditional metrics overlook and gives every student — regardless of background — a way to demonstrate readiness. The equity-gap analytics help administrators find and close disparities between student populations, turning a fairness commitment into a measurable, monitored practice.",
      },
    ],
  },
];

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };

export default function FAQ() {
  const [active, setActive] = useState("general");
  const current = CATEGORIES.find((c) => c.id === active);

  return (
    <div className="min-h-screen bg-white">
      <MarketingNav />

      {/* HERO */}
      <section className="brand-hero relative overflow-hidden pt-32 pb-16 text-white">
        <img src={PAGE_BANNERS.faq} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#00243f]/92 via-[#00243f]/85 to-[#003057]/70" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#a8e063]">
            <HelpCircle className="h-3.5 w-3.5" /> Frequently Asked Questions
          </span>
          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Everything you want to know about <span className="text-[#78be21]">GPA+</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
            Answers grouped by who you are — plus the story of why we built GPA+ and what makes it different.
          </p>
        </div>
      </section>

      {/* CATEGORY TABS */}
      <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
        <p className="mb-5 text-center text-sm font-bold uppercase tracking-[0.2em] text-[#5fa114]">
          Choose your role to see relevant answers
        </p>
        <div className="flex flex-wrap justify-center gap-3" data-testid="faq-category-tabs">
          {CATEGORIES.map((c) => {
            const on = c.id === active;
            return (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`faq-tab-${c.id}`}
                className={`inline-flex items-center gap-2.5 rounded-xl border-2 px-6 py-3.5 text-sm font-bold shadow-sm transition-all duration-200 hover:-translate-y-0.5 ${
                  on
                    ? "border-[#003057] bg-[#003057] text-white shadow-lg ring-2 ring-[#78be21]/40"
                    : "border-slate-200 bg-white text-slate-700 hover:border-[#78be21] hover:text-[#003057] hover:shadow-md"
                }`}
              >
                <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${on ? "bg-[#78be21]/25" : "bg-[#003057]/6"}`}>
                  <c.icon className={`h-4 w-4 ${on ? "text-[#a8e063]" : "text-[#003057]"}`} />
                </span>
                {c.label}
              </button>
            );
          })}
        </div>

        <motion.div
          key={active}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="mx-auto mt-10 max-w-3xl"
        >
          <motion.p variants={item} className="mb-6 text-center text-sm font-semibold uppercase tracking-widest text-[#5fa114]">
            {current.blurb}
          </motion.p>
          <motion.div variants={item}>
            <Accordion type="single" collapsible className="space-y-3" data-testid={`faq-list-${active}`}>
              {current.faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${active}-${i}`}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white px-5 card-elevate"
                >
                  <AccordionTrigger className="py-5 text-left font-heading text-base font-bold text-[#003057] hover:no-underline sm:text-lg">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-base leading-relaxed text-slate-600">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="bg-slate-50 py-16">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-[#003057] sm:text-3xl">Still curious? See it in action.</h2>
          <p className="mt-3 text-lg text-slate-600">
            The fastest way to understand GPA+ is to explore the working demo across all four roles.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/login">
              <Button data-testid="faq-try-demo" size="lg" className="rounded-full bg-[#78be21] px-7 text-base font-bold text-[#00243f] hover:bg-[#6aac1c]">
                Try the working demo <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/framework">
              <Button data-testid="faq-explore-framework" size="lg" variant="outline" className="rounded-full border-[#003057]/20 px-7 text-base font-bold text-[#003057] hover:bg-[#003057]/5">
                Explore the framework
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
